/**
 * Core Task Migration Query Hooks (Epic 6 — Migratie & livegang, Story 6.3)
 *
 * Zelfde opbouw-patroon als `useCoreTasks.ts` (Story 2.2): useQuery voor
 * lezen, useMutation + invalidateQueries voor schrijven.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coreTaskMigrationApi } from '../../lib/coreTaskMigrationApi';

export const coreTaskMigrationKeys = {
  all: ['coreTaskMigration'] as const,
  status: () => [...coreTaskMigrationKeys.all, 'status'] as const,
  impactReport: () => [...coreTaskMigrationKeys.all, 'impactReport'] as const,
};

export function useMigrationStatus() {
  return useQuery({
    queryKey: coreTaskMigrationKeys.status(),
    queryFn: () => coreTaskMigrationApi.status(),
  });
}

export function useImpactReport(enabled: boolean) {
  return useQuery({
    queryKey: coreTaskMigrationKeys.impactReport(),
    queryFn: () => coreTaskMigrationApi.impactReport(),
    enabled,
  });
}

export function useRunDryRun() {
  return useMutation({
    mutationFn: (migrationMoment: string) => coreTaskMigrationApi.dryRun(migrationMoment),
  });
}

export function useRunCommit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (migrationMoment: string) => coreTaskMigrationApi.commit(migrationMoment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coreTaskMigrationKeys.status() });
    },
  });
}

export function useDeactivateMigration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => coreTaskMigrationApi.deactivate(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coreTaskMigrationKeys.status() });
    },
  });
}

export function useReviewImpactReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => coreTaskMigrationApi.reviewImpactReport(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coreTaskMigrationKeys.status() });
    },
  });
}

export function usePublishManual() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => coreTaskMigrationApi.publishManual(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coreTaskMigrationKeys.status() });
    },
  });
}
